import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GameService } from '../../services/game.service';
import { ContentService } from '../../services/content.service';
import { FilterDataService } from '../../services/filter-data.service';
import { environment } from '../../../../environments/environment';
import { StorageKey, StorageService } from '../../services/storage.service';
import { Attributes, CollectionResponse } from '../../models/Strapi';
import { WelcomeText } from '../../models/WelcomeText';
import { GeneralData } from '../../models/General';
import { Mission } from '../../models/Missions';
import { combineLatestWith, interval, map, pipe, Subscription } from 'rxjs';

@Component({
    selector: 'app-background',
    templateUrl: './background.component.html',
    styleUrls: ['./background.component.scss'],
    standalone: false
})
export class BackgroundComponent implements OnInit, OnDestroy {
  @Input() koffer = false;
  @Input() justBackground = false;
  @Input() canSkipDay = false;
  @Input() marginTop = 150;

  currentlyShowingMissions = [];
  kofferOpened = false;
  env = environment;
  finishedMissionsText = '';
  firstLogin = false;
  kofferImg: string;
  endDate: Date;
  kofferCompletion = 0;
  finishedMissionsData?: CollectionResponse<Mission>['data'];
  general?: Attributes<GeneralData>;
  welcomeText?: Attributes<WelcomeText>;

  private kofferClosedBagImg = 'closed-bag-1.svg';
  private kofferOpenedBagImg: string;

  private currentlyShowingMissionSubscription?: Subscription;
  private kofferCompletionSubscription?: Subscription;

  constructor(
    public gameService: GameService,
    public contentService: ContentService,
    public filterDataService: FilterDataService,
    private storageService: StorageService,
  ) {
    this.endDate = this.storageService.get(StorageKey.END_DATE);
  }

  ngOnInit() {

    this.currentlyShowingMissionSubscription = this.filterDataService.currentlyShowingMissions.subscribe((r) => {
      this.currentlyShowingMissions = r;
      // Log: !gameService.showWelcomeMessage && !koffer && !gameService.showEndDateRequest
       console.log('showWelcomeMessage: ', this.gameService.showWelcomeMessage);
      console.log('koffer: ', this.koffer);
      console.log('showEndDateRequest: ', this.gameService.showEndDateRequest);
      console.log('firstLogin: ', this.firstLogin);

    });

    this.kofferCompletionSubscription =
      this.filterDataService.finishedMission
        .pipe(combineLatestWith(this.gameService.missionsToUpgrade))
        .subscribe(([finished, toUpgrade]) =>
          toUpgrade == 0 ? this.kofferCompletion = 0 : this.kofferCompletion = finished.length / toUpgrade);

    const daysUntilEndDate = this.gameService.calculateDateDifference(this.storageService.get(StorageKey.END_DATE), new Date());
    this.firstLogin = !this.storageService.has(StorageKey.FIRST_LOGIN_DATE);
    this.gameService.showWelcomeMessage = this.firstLogin;

    this.gameService.setBackgroundBasedOnDate();
    this.showEndDateRequestBasedOnDate();
    this.showWelcomeMessageBasedOnDate();
    this.filterDataService.calculateSliceAmount(daysUntilEndDate);
    this.filterDataService.loadMissions();
    this.gameService.toggleSecondDayEndRequest();

    this.contentService.getGeneral().subscribe((r) => {
      this.general = r.data.attributes;
    });

    this.pickWelcomeText();
    this.pickFinishedMissionText();


    if (this.koffer) {
      this.contentService.getMissionsByIds(Object.values(this.filterDataService.finishedMission.value)).subscribe((r) => {
        this.finishedMissionsData = r.data;
      });
    }

    this.filterDataService.finishedMission.subscribe((_) => this.upgradeKoffer());
  }

  ngOnDestroy() {
    this.currentlyShowingMissionSubscription?.unsubscribe();
    this.kofferCompletionSubscription?.unsubscribe();
  }

  //shows the conductor's end date request based on date
  showEndDateRequestBasedOnDate(): void {
    const week = this.gameService.getDateDifferenceInWeeks(this.storageService.get(StorageKey.SHOW_END_DATE_REQUEST));

    if (!this.storageService.has(StorageKey.END_DATE)) {
      const startDateDifference = this.gameService.calculateDateDifference(this.storageService.get(StorageKey.START_DATE), new Date());

      if (!this.storageService.has(StorageKey.SHOW_END_DATE_REQUEST)) {
        this.gameService.showEndDateRequest = false;
        this.storageService.set(StorageKey.SHOW_END_DATE_FIRST_REQUEST, true);
      }

      if (startDateDifference > 7 && week >= 1 && !this.firstLogin) {
        // this.gameService.turnOffShowWelcome()
        this.gameService.turnOnEndDateRequest();
        this.storageService.set(StorageKey.SHOW_END_DATE_REQUEST, new Date());
      }

      if (
        startDateDifference > 7 &&
        !this.storageService.has(StorageKey.END_DATE) &&
        this.storageService.get(StorageKey.SHOW_END_DATE_FIRST_REQUEST) &&
        !this.firstLogin
      ) {
        // this.gameService.turnOffShowWelcome();
        this.gameService.turnOnEndDateRequest();
        this.storageService.set(StorageKey.SHOW_END_DATE_REQUEST, new Date());
      }
    }
  }

  //TODO: if empty missions, show message
  toggleShowKoffer(): void {
    this.kofferOpened = !this.kofferOpened;
    if (this.kofferOpened) {
      this.kofferImg = this.kofferOpenedBagImg;
    } else {
      this.kofferImg = this.kofferClosedBagImg;
    }
  }

  //function that upgrades koffer based on finished missions, limits the amount of missions that can be seen in the counter
  upgradeKoffer(): void {
    const kofferStages = [
      {
        missionsRequired: 0,
        closedBagImg: 'closed-bag-1.svg',
        openBagImg: 'open-bag-1.svg',
      },
      {
        missionsRequired: 5,
        closedBagImg: 'closed-bag-2.svg',
        openBagImg: 'open-bag-2.svg',
      },
      {
        missionsRequired: 15,
        closedBagImg: 'koffer-closed.svg',
        openBagImg: 'koffer-opened.svg',
      },
      {
        missionsRequired: 40,
        closedBagImg: 'koffer-closed.svg',
        openBagImg: 'koffer-opened.svg',
      },
    ];

    let currentStage = kofferStages[0];
    const missionsCompleted = this.filterDataService.finishedMission.value.length;

    for (const kofferStage of kofferStages) {
      if (missionsCompleted - 1 >= kofferStage.missionsRequired) {
        currentStage = kofferStage;
      }
    }

    const stageIdx = kofferStages.indexOf(currentStage);
    const nextStage = stageIdx === kofferStages.length - 1 ? null : kofferStages[kofferStages.indexOf(currentStage) + 1];

    this.gameService.missionsToUpgrade.next(
      nextStage?.missionsRequired ??
        this.contentService.allMissions?.data?.length ??
        kofferStages[kofferStages.length - 1].missionsRequired,
    );
    this.kofferClosedBagImg = currentStage.closedBagImg;
    this.kofferOpenedBagImg = currentStage.openBagImg;
    this.kofferImg = this.kofferClosedBagImg;
  }

  //determines when to show the conductor's welcome message
  showWelcomeMessageBasedOnDate(): void {
    const welcomeDate = this.storageService.get(StorageKey.WELCOME_MESSAGE_DATE);

    if (!welcomeDate || this.gameService.calculateDateDifference(welcomeDate, new Date()) >= 2) {
      this.gameService.showWelcomeMessage = true;
      this.storageService.set(StorageKey.WELCOME_MESSAGE_DATE, new Date());
    }
  }

  // turnOffFirstLoginCloudText(): void {
  //   this.firstLogin = false;
  //   localStorage.setItem('firstLoginDate', String(new Date()));
  //   this.gameService.showWelcomeMessage = false;
  //   this.gameService.showEndDateRequest = false;
  // }

  //send user to next day
  skipDay(): void {
    this.gameService.skipDay();
  }

  private pickFinishedMissionText() {
    this.contentService.getFinishedMissionsText().subscribe((r) => {
      const options = [
        r.data.attributes.finishedMissionTextOne,
        r.data.attributes.finishedMissionTextTwo,
        r.data.attributes.finishedMissionTextThree,
        r.data.attributes.finishedMissionTextFour,
      ];

      this.finishedMissionsText = options[Math.floor(Math.random() * options.length)];
    });
  }

  private pickWelcomeText() {
    this.contentService.getWelcomeText().subscribe((r) => {
      this.welcomeText = r.data[Math.floor(Math.random() * r.data.length)].attributes;
    });
  }
}
